import { useParams } from "react-router";
import { PageLayout } from "../../components/page-layout";
import { Column, Row, breakPoints } from "../../components/grid";
import { FilePreview } from "../../components/file-preview";
import { useTranslation } from "react-i18next";
import { useToast } from "../../components/toast";
import { useQdsSignDetail } from "./useQdsSignDetail";
import { QdsActionButtons } from "./QdsActionButtons";
import { QdsMetadataPreview } from "./QdsMetadataPreview";
import { GiveConsentModal } from "./GiveConsentModal";
import { QdsSignModal } from "../web-qds-signature/QdsSignModal";
import { useState } from "react";
import DigitalSignatureQualifiedGateway from "../../api/gateways/DigitalSignatureQualifiedGateway";
import DocumentGateway from "../../api/gateways/DocumentGateway";
import "./QdsSignDetail.styles.css";

export function QdsSignDetailScreen() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { document, qdsRequest, loading, currentUser, windowHeight, windowWidth, refetch, signingOrder, nextSignerEmail, nextSignerPrio } =
    useQdsSignDetail(id || "");
  const { success, danger } = useToast();

  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [showSignDocumentModal, setShowSignDocumentModal] = useState(false);
  const [signDocumentLoading, setSignDocumentLoading] = useState(false);

  const handleResendEmail = async (signatureId: number, signatureType: "consent" | "holder" | "counterparty", email: string) => {
    try {
      if (signatureType === "consent") {
        await DigitalSignatureQualifiedGateway.resendConsentEmail(signatureId);
      } else if (signatureType === "holder") {
        await DigitalSignatureQualifiedGateway.resendHolderSignatureEmail(signatureId);
      } else if (signatureType === "counterparty") {
        await DigitalSignatureQualifiedGateway.resendCounterPartyEmail(signatureId);
      }
      success(`Email je uspešno poslat na ${email}`);
      refetch();
    } catch (error: any) {
      danger(error?.message || t("ERRORS.UNKNOWN"));
    }
  };

  const handleSignDocument = async () => {
    setShowSignDocumentModal(true);
  };

  const handleDownloadDocument = async () => {
    if (!qdsRequest || !document) return;

    try {
      const lastFileUrl = qdsRequest.document?.files?.at(-1)?.url;

      if (!lastFileUrl) {
        throw new Error("File URL nije pronađen");
      }
      
      const response = await DocumentGateway.getDocumentFile(
        lastFileUrl
      );

      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = docFile?.name || "document.pdf";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      danger(t("ERRORS.UNKNOWN"));
    }
  };

  const handleSignDocumentSubmit = async (file: File) => {
    if (!qdsRequest || !currentUser) return;

    setSignDocumentLoading(true);
    setShowSignDocumentModal(false);

    try {
      const userId = currentUser.backEndId ? Number(currentUser.backEndId) : undefined;
      if (!userId) {
        danger(t("ERRORS.UNKNOWN"));
        setSignDocumentLoading(false);
        return;
      }

      await DigitalSignatureQualifiedGateway.signDocument(
        qdsRequest.id,
        userId,
        "signed",
        file
      );
      success("Dokument je uspešno potpisan");

      const signChannel = new BroadcastChannel('qds_signature_update');
      signChannel.postMessage({ qdsRequestId: qdsRequest.id });
      signChannel.close();

      setTimeout(() => {
        refetch();
        setSignDocumentLoading(false);
      }, 1500);
    } catch (error: any) {
      danger(error?.message || t("ERRORS.UNKNOWN"));
      setSignDocumentLoading(false);
    }
  };

  const handleGiveConsent = () => {
    setShowConsentModal(true);
  };

  const handleApproveConsent = async () => {
    if (!qdsRequest || !currentUser) return;

    setConsentLoading(true);
    try {
      const userId = currentUser.backEndId ? Number(currentUser.backEndId) : undefined;
      if (!userId) {
        danger(t("ERRORS.UNKNOWN"));
        return;
      }

      await DigitalSignatureQualifiedGateway.approveConsent(qdsRequest.id, userId);
      success(t("DOCUMENTS.REQUEST_SUCCESS"));
      setShowConsentModal(false);

      const consentChannel = new BroadcastChannel('qds_signature_update');
      consentChannel.postMessage({ qdsRequestId: qdsRequest.id });
      consentChannel.close();

      refetch();
    } catch (error) {
      danger(t("ERRORS.UNKNOWN"));
    } finally {
      setConsentLoading(false);
    }
  };

  const handleRejectConsent = async () => {
    if (!qdsRequest || !currentUser) return;

    setConsentLoading(true);
    try {
      const userId = currentUser.backEndId ? Number(currentUser.backEndId) : undefined;
      if (!userId) {
        danger(t("ERRORS.UNKNOWN"));
        return;
      }

      await DigitalSignatureQualifiedGateway.rejectConsent(qdsRequest.id, userId);
      success(t("DOCUMENTS.REQUEST_SUCCESS"));
      setShowConsentModal(false);

      const rejectChannel = new BroadcastChannel('qds_signature_update');
      rejectChannel.postMessage({ qdsRequestId: qdsRequest.id });
      rejectChannel.close();

      refetch();
    } catch (error) {
      danger(t("ERRORS.UNKNOWN"));
    } finally {
      setConsentLoading(false);
    }
  };

  const lastFileIndex = (document?.files?.length || 0) - 1;
  const docFile = document?.files?.[lastFileIndex] && lastFileIndex >= 0
    ? {
      uri: document.files[lastFileIndex].url,
      type: document.files[lastFileIndex].mimeType || "",
      name: document.files[lastFileIndex].name,
    }
    : null;

  return (
    <PageLayout
      title={`${t("DOCUMENTS.QDS_SIGNING")} #${document?.metaData?.subjectNumber || id}`}
      backBtn
      titleRight={
        qdsRequest ? (
          <div className="d-flex flex-row align-items-center justify-content-end mr-3">
            <QdsActionButtons
              qdsRequest={qdsRequest}
              currentUser={currentUser}
              onSignDocument={handleSignDocument}
              onGiveConsent={handleGiveConsent}
              signingDocument={signDocumentLoading}
              givingConsent={false}
              signingOrder={signingOrder}
            />
          </div>
        ) : undefined
      }
    >
      {document ? (
        <Row>
          <Column>
            {docFile ? (
              <div
                className="d-flex flex-column align-items-center mb-2"
                style={{ position: "sticky", left: 0, top: "128px" }}
              >
                <FilePreview
                  classNameContainer="document-preview-container"
                  enableCanceling={false}
                  enableEditing={false}
                  type={docFile?.type}
                  url={docFile?.uri}
                  name={docFile?.name}
                  width={breakPoints.m < windowWidth ? windowWidth * 0.5 : windowWidth - 64}
                  height={windowHeight - 282}
                />
              </div>
            ) : null}
          </Column>
          <Column>
            <QdsMetadataPreview 
              qdsRequest={qdsRequest} 
              onResendEmail={handleResendEmail}
              signingOrder={signingOrder}
              currentUserId={currentUser?.backEndId ? Number(currentUser.backEndId) : undefined}
              nextSignerEmail={nextSignerEmail}
              nextSignerPrio={nextSignerPrio}
            />
          </Column>
        </Row>
      ) : loading ? null : (
        <h4>{t("DOCUMENTS.NO_FOUND")}</h4>
      )}

      <GiveConsentModal
        isVisible={showConsentModal}
        qdsRequest={qdsRequest}
        onDismiss={() => setShowConsentModal(false)}
        onApprove={handleApproveConsent}
        onReject={handleRejectConsent}
        isLoading={consentLoading}
      />

      {showSignDocumentModal && qdsRequest && (
        <QdsSignModal
          documentId={document?.id}
          qdsRequestId={qdsRequest.id}
          documentName={docFile?.name}
          onClose={() => setShowSignDocumentModal(false)}
          onConfirm={handleSignDocumentSubmit}
          onDownload={handleDownloadDocument}
        />
      )}
    </PageLayout>
  );
}
